<template>
  <!-- ICONIC: einen Kanal ansehen, ändern, anlegen, sortieren, löschen; Rollen im Kanal -->
  <v-card outlined>
    <template v-if="isNew || channel">
      <v-card-title>
        <span class="text-truncate">{{ isNew ? "Neuer Kanal" : channel.channelName }}</span>
        <v-icon v-if="!isNew && isDefault" small class="ml-2" title="Standardkanal">mdi-home</v-icon>
        <v-spacer></v-spacer>
        <v-btn icon small aria-label="Schließen" @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-subtitle v-if="!isNew" class="pb-2">
        {{ path }}<span v-if="isDefault"> · Standardkanal: hier landet, wer neu kommt</span>
      </v-card-subtitle>

      <v-card-text>
        <!-- Leute im Kanal -->
        <div v-if="!isNew && peopleHere.length" class="mb-4">
          <div class="text-overline">Gerade hier ({{ peopleHere.length }})</div>
          <v-chip
            v-for="p in peopleHere"
            :key="p.clid"
            small
            outlined
            class="mr-1 mb-1"
            @click="$emit('select-person', p.clid)"
            >{{ p.clientNickname }}</v-chip
          >
        </div>

        <!-- Einstellungen -->
        <div class="text-overline">{{ isNew ? "Angaben" : "Einstellungen" }}</div>
        <v-form ref="form" @submit.prevent="save">
          <v-autocomplete
            v-if="isNew"
            v-model="form.parent"
            :items="parentItems"
            item-text="name"
            item-value="cid"
            label="Wo soll der Kanal hin?"
            outlined
            dense
          ></v-autocomplete>
          <v-text-field
            v-model="form.name"
            label="Name"
            outlined
            dense
            counter="40"
            maxlength="40"
            :rules="[(v) => !!String(v || '').trim() || 'Bitte einen Namen eingeben']"
          ></v-text-field>
          <template v-if="!spacer">
            <v-text-field
              v-model="form.topic"
              label="Thema (kurze Zeile unter dem Namen)"
              outlined
              dense
              maxlength="255"
            ></v-text-field>
            <v-textarea
              v-model="form.description"
              label="Beschreibung"
              outlined
              dense
              auto-grow
              rows="2"
            ></v-textarea>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.password"
                  :label="hasPassword ? 'Neues Passwort (leer = bleibt)' : 'Passwort (leer = ohne)'"
                  outlined
                  dense
                  autocomplete="new-password"
                  type="password"
                  :disabled="form.removePassword"
                ></v-text-field>
                <v-checkbox
                  v-if="hasPassword"
                  v-model="form.removePassword"
                  label="Passwort entfernen"
                  dense
                  hide-details
                  class="mt-n3 mb-3"
                ></v-checkbox>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="form.maxclients"
                  label="Plätze (leer = unbegrenzt)"
                  type="number"
                  min="1"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-select
              v-model="form.talkPower"
              :items="talkItems"
              label="Wer darf sprechen?"
              outlined
              dense
            ></v-select>
          </template>
          <div class="d-flex justify-end">
            <v-btn color="primary" depressed type="submit" :loading="busy" :disabled="!dirty">
              {{ isNew ? "Kanal anlegen" : "Speichern" }}
            </v-btn>
          </div>
        </v-form>

        <!-- Rollen im Kanal -->
        <template v-if="!isNew && !spacer">
          <v-divider class="my-4"></v-divider>
          <div class="d-flex align-center">
            <div class="text-overline">Rollen in diesem Kanal</div>
            <v-spacer></v-spacer>
            <v-btn small text color="primary" @click="openRoleDialog"><v-icon left small>mdi-plus</v-icon>Rolle geben</v-btn>
          </div>
          <p v-if="!roles.length" class="text-body-2 mb-0">Niemand hat hier eine besondere Rolle.</p>
          <v-list v-else dense class="py-0">
            <v-list-item v-for="r in roles" :key="r.cldbid" class="px-0">
              <v-list-item-content>
                <v-list-item-title>
                  <router-link :to="{ name: 'user', params: { cldbid: r.cldbid } }">{{ r.name }}</router-link>
                </v-list-item-title>
                <v-list-item-subtitle>{{ r.label }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn small text color="error" :disabled="busy || isProtected(r.cldbid)" @click="removeRole(r)">Wegnehmen</v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </template>

        <!-- Weitere Aktionen -->
        <template v-if="!isNew">
          <v-divider class="my-4"></v-divider>
          <div class="text-overline">Mehr</div>
          <div class="more">
            <v-btn outlined small :disabled="!canUp || busy" @click="move(-1)"><v-icon left small>mdi-arrow-up</v-icon>Nach oben</v-btn>
            <v-btn outlined small :disabled="!canDown || busy" @click="move(1)"><v-icon left small>mdi-arrow-down</v-icon>Nach unten</v-btn>
            <v-btn v-if="!spacer" outlined small @click="$emit('new-sub', channel.cid)"><v-icon left small>mdi-subdirectory-arrow-right</v-icon>Unterkanal</v-btn>
            <v-btn outlined small color="error" :disabled="isDefault || busy" @click="askDelete = true"><v-icon left small>mdi-delete-outline</v-icon>Löschen</v-btn>
          </div>
        </template>
      </v-card-text>
    </template>

    <template v-else>
      <v-card-title>
        Kanal nicht mehr da
        <v-spacer></v-spacer>
        <v-btn icon small aria-label="Schließen" @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
    </template>

    <!-- Rolle geben -->
    <v-dialog v-model="roleDialog.open" max-width="520" scrollable>
      <v-card>
        <v-card-title>Rolle in «{{ channel && channel.channelName }}» geben</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="roleDialog.cldbid"
            :items="userItems"
            :loading="usersLoading"
            item-text="name"
            item-value="cldbid"
            label="Wer?"
            outlined
            dense
            no-data-text="Niemand gefunden"
          ></v-autocomplete>
          <v-radio-group v-model="roleDialog.role" class="mt-0" hide-details>
            <v-radio v-for="role in groups.channelRoles" :key="role.key" :value="role.key" class="align-start mb-3">
              <template #label>
                <div>
                  <div class="font-weight-medium text--primary">{{ role.label }}</div>
                  <div class="text-body-2">{{ role.text }}</div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="roleDialog.open = false">Abbrechen</v-btn>
          <v-btn color="primary" depressed :loading="busy" :disabled="!roleDialog.cldbid || !roleDialog.role" @click="giveRole">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Löschen -->
    <v-dialog v-model="askDelete" max-width="460">
      <v-card v-if="channel">
        <v-card-title>Kanal löschen?</v-card-title>
        <v-card-text class="text-body-1">
          «{{ channel.channelName }}» wird gelöscht<span v-if="subCount">, zusammen mit {{ subCount }} Unterkanal/Unterkanälen</span>.
          <span v-if="familyPeople"> {{ familyPeople }} Person(en) darin landen im Standardkanal.</span>
          Das lässt sich nicht rückgängig machen.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="askDelete = false">Abbrechen</v-btn>
          <v-btn color="error" depressed :loading="busy" @click="remove">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { asList, channelPaths, channelRoleLabel, ensureKanalChef, errorText } from "@/iconic/rechte";
import { parseSpacer, moveParams } from "@/iconic/live";

const flag = (v) => v === true || v === 1 || v === "1";
let dbCache = null;

export default {
  props: {
    channel: Object,
    parentCid: String,
    groups: Object,
    channels: Array,
    clients: Array,
    config: Object,
    serverInfo: Object,
  },
  data() {
    return {
      busy: false,
      info: {},
      roles: [],
      form: this.emptyForm(),
      original: null,
      askDelete: false,
      roleDialog: { open: false, cldbid: null, role: "chef" },
      users: [],
      usersLoading: false,
    };
  },
  computed: {
    isNew() {
      return !this.channel && this.parentCid !== null && this.parentCid !== undefined;
    },
    spacer() {
      return this.channel ? parseSpacer(this.channel.channelName) : null;
    },
    isDefault() {
      return this.channel && flag(this.channel.channelFlagDefault);
    },
    hasPassword() {
      return this.channel && flag(this.channel.channelFlagPassword);
    },
    path() {
      const c = channelPaths(this.channels).find((x) => x.cid === String(this.channel.cid));
      return c ? c.path : this.channel.channelName;
    },
    peopleHere() {
      return this.clients.filter((p) => String(p.cid) === String(this.channel.cid));
    },
    descendants() {
      const out = [];
      const walk = (cid) =>
        this.channels.filter((c) => String(c.pid) === String(cid)).forEach((c) => {
          out.push(String(c.cid));
          walk(c.cid);
        });
      if (this.channel) walk(this.channel.cid);
      return out;
    },
    subCount() {
      return this.descendants.length;
    },
    familyPeople() {
      const ids = [String(this.channel.cid), ...this.descendants];
      return this.clients.filter((p) => ids.includes(String(p.cid))).length;
    },
    canUp() {
      return this.channel && !!moveParams(this.channels, this.channel, -1);
    },
    canDown() {
      return this.channel && !!moveParams(this.channels, this.channel, 1);
    },
    parentItems() {
      return [{ cid: "0", name: "Ganz oben (eigener Kanal)" }].concat(
        channelPaths(this.channels).map((c) => ({ cid: c.cid, name: `Unter ${c.path}` }))
      );
    },
    talkItems() {
      const items = [
        { text: "Alle", value: 0 },
        { text: "Ab «Darf sprechen»", value: 25 },
        { text: "Ab «Helfer»", value: 50 },
        { text: "Ab «Kanal-Verwalter»", value: 60 },
      ];
      const v = Number(this.form.talkPower);
      if (!items.some((i) => i.value === v)) items.push({ text: `Eigener Wert ${v}`, value: v });
      return items;
    },
    userItems() {
      return this.users;
    },
    dirty() {
      if (this.isNew) return Boolean(String(this.form.name || "").trim());
      return this.original && JSON.stringify(this.form) !== JSON.stringify(this.original);
    },
  },
  watch: {
    "channel.cid": { immediate: true, handler() { this.loadDetails(); } },
    parentCid: { immediate: true, handler() { if (this.isNew) this.resetNew(); } },
  },
  methods: {
    emptyForm() {
      return { parent: "0", name: "", topic: "", description: "", password: "", removePassword: false, maxclients: "", talkPower: 0 };
    },
    isProtected(cldbid) {
      return this.config.protectedDbIds.includes(String(cldbid));
    },
    resetNew() {
      this.form = { ...this.emptyForm(), parent: String(this.parentCid || "0") };
      this.original = null;
      this.roles = [];
    },
    async loadDetails() {
      if (!this.channel) return;
      const cid = this.channel.cid;
      try {
        const info = asList(await this.$TeamSpeak.execute("channelinfo", { cid }))[0] || {};
        if (!this.channel || String(this.channel.cid) !== String(cid)) return;
        this.info = info;
        const unlimited = flag(info.channelFlagMaxclientsUnlimited);
        this.form = {
          parent: String(this.channel.pid),
          name: info.channelName || this.channel.channelName,
          topic: info.channelTopic || "",
          description: info.channelDescription || "",
          password: "",
          removePassword: false,
          maxclients: unlimited || Number(info.channelMaxclients) < 0 ? "" : Number(info.channelMaxclients),
          talkPower: Number(info.channelNeededTalkPower) || 0,
        };
        this.original = JSON.parse(JSON.stringify(this.form));
        await this.loadRoles();
      } catch (err) {
        this.$toast.error(errorText(err));
      }
    },
    async loadRoles() {
      if (!this.channel || this.spacer) { this.roles = []; return; }
      const list = asList(await this.$TeamSpeak.execute("channelgroupclientlist", { cid: this.channel.cid }))
        .filter((a) => String(a.cgid) !== this.groups.defaultCg);
      const out = [];
      for (const a of list) {
        let name = `Benutzer ${a.cldbid}`;
        try {
          name = asList(await this.$TeamSpeak.execute("clientdbinfo", { cldbid: a.cldbid }))[0].clientNickname || name;
        } catch (e) { /* gelöschter Benutzer */ }
        out.push({ cldbid: String(a.cldbid), cgid: String(a.cgid), name, label: channelRoleLabel(this.groups, a.cgid) });
      }
      this.roles = out.sort((x, y) => x.name.localeCompare(y.name));
    },
    props() {
      const f = this.form;
      const p = {};
      const changed = (k) => this.isNew || f[k] !== this.original[k];
      if (changed("name")) p.channel_name = String(f.name).trim();
      if (!this.spacer) {
        if (changed("topic")) p.channel_topic = f.topic;
        if (changed("description")) p.channel_description = f.description;
        if (f.removePassword) p.channel_password = "";
        else if (f.password) p.channel_password = f.password;
        if (changed("maxclients")) {
          if (f.maxclients === "" || f.maxclients === null || Number(f.maxclients) <= 0) {
            p.channel_flag_maxclients_unlimited = 1;
          } else {
            p.channel_flag_maxclients_unlimited = 0;
            p.channel_maxclients = Number(f.maxclients);
          }
        }
        if (changed("talkPower")) p.channel_needed_talk_power = Number(f.talkPower) || 0;
      }
      return p;
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.busy = true;
      try {
        const p = this.props();
        if (this.isNew) {
          const r = asList(await this.$TeamSpeak.execute("channelcreate", { ...p, cpid: this.form.parent, channel_flag_permanent: 1 }))[0] || {};
          this.$toast.success(`Kanal «${p.channel_name}» angelegt.`);
          this.$emit("changed");
          if (r.cid) this.$emit("select-channel", r.cid);
        } else {
          if (Object.keys(p).length) await this.$TeamSpeak.execute("channeledit", { cid: this.channel.cid, ...p });
          this.$toast.success("Gespeichert.");
          this.$emit("changed");
          await this.loadDetails();
        }
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      this.busy = false;
    },
    async move(dir) {
      const params = moveParams(this.channels, this.channel, dir);
      if (!params) return;
      this.busy = true;
      try {
        await this.$TeamSpeak.execute("channelmove", params);
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      this.busy = false;
    },
    async remove() {
      this.busy = true;
      try {
        const name = this.channel.channelName;
        await this.$TeamSpeak.execute("channeldelete", { cid: this.channel.cid, force: 1 });
        this.askDelete = false;
        this.$toast.success(`Kanal «${name}» gelöscht.`);
        this.$emit("changed");
        this.$emit("close");
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      this.busy = false;
    },
    async openRoleDialog() {
      this.roleDialog = { open: true, cldbid: null, role: "chef" };
      if (dbCache) { this.users = dbCache; return; }
      this.usersLoading = true;
      try {
        const list = await this.$TeamSpeak.fullClientDBList();
        dbCache = list
          .map((c) => ({ cldbid: String(c.cldbid), name: c.clientNickname }))
          .filter((c) => !this.isProtected(c.cldbid))
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        this.users = dbCache;
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      this.usersLoading = false;
    },
    async giveRole() {
      const { cldbid, role: key } = this.roleDialog;
      const role = this.groups.channelRoles.find((r) => r.key === key);
      this.busy = true;
      try {
        const cgid = key === "chef" ? await ensureKanalChef(this.groups) : role.cgid;
        await this.$TeamSpeak.execute("setclientchannelgroup", { cgid, cid: this.channel.cid, cldbid });
        this.roleDialog.open = false;
        this.$toast.success("Gespeichert.");
        this.$emit("changed");
        await this.loadRoles();
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      this.busy = false;
    },
    async removeRole(r) {
      this.busy = true;
      try {
        await this.$TeamSpeak.execute("setclientchannelgroup", { cgid: this.groups.defaultCg, cid: this.channel.cid, cldbid: r.cldbid });
        this.$toast.success(`${r.name} ist hier kein ${r.label} mehr.`);
        this.$emit("changed");
        await this.loadRoles();
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      this.busy = false;
    },
  },
};
</script>

<style scoped>
.more {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
</style>
